import { useState } from 'react';
import { X, Delete, Check } from 'lucide-react';

export function Numpad({ value, onChange, onConfirm, onCancel, title, subtitle, maxValue = 9999, allowHalf = false }) {
  const [display, setDisplay] = useState(value?.toString() || '0');

  const handleKey = (key) => {
    if (key === 'clear') {
      setDisplay('0');
      onChange?.(0);
    } else if (key === 'backspace') {
      const newVal = display.endsWith('.5')
        ? (display.slice(0, -2) || '0')
        : display.length > 1 ? display.slice(0, -1) : '0';
      setDisplay(newVal);
      onChange?.(parseFloat(newVal) || 0);
    } else if (key === 'half') {
      const current = parseFloat(display) || 0;
      if (current % 1 !== 0) {
        const newVal = Math.floor(current).toString();
        setDisplay(newVal);
        onChange?.(Math.floor(current));
      } else if (current + 0.5 <= maxValue) {
        const newVal = (current + 0.5).toString();
        setDisplay(newVal);
        onChange?.(current + 0.5);
      }
    } else {
      if (display.endsWith('.5')) return;
      const newVal = display === '0' ? key : display + key;
      const numVal = parseFloat(newVal) || 0;
      if (numVal <= maxValue) {
        setDisplay(newVal);
        onChange?.(numVal);
      }
    }
  };

  const handleConfirm = () => {
    onConfirm?.(parseFloat(display) || 0);
  };

  const isHalf = display.endsWith('.5');

  return (
    <div className="modal-overlay" onClick={onCancel} data-testid="numpad-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="numpad-modal">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            {title && <h2 className="text-2xl font-bold text-zinc-900">{title}</h2>}
            {subtitle && <p className="text-lg text-zinc-600">{subtitle}</p>}
          </div>
          <button
            onClick={onCancel}
            className="h-14 w-14 flex items-center justify-center rounded-full bg-zinc-100 active:bg-zinc-200"
            data-testid="numpad-cancel"
          >
            <X className="w-7 h-7 text-zinc-600" />
          </button>
        </div>

        {/* Display */}
        <div className="input-display mb-6" data-testid="numpad-display">
          {display}
        </div>

        {/* Numpad Grid */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
            <button
              key={num}
              className="numpad-key"
              onClick={() => handleKey(num)}
              data-testid={`numpad-key-${num}`}
            >
              {num}
            </button>
          ))}
          <button
            className="numpad-key"
            onClick={() => handleKey('clear')}
            data-testid="numpad-key-clear"
          >
            C
          </button>
          <button
            className="numpad-key"
            onClick={() => handleKey('0')}
            data-testid="numpad-key-0"
          >
            0
          </button>
          <button
            className="numpad-key"
            onClick={() => handleKey('backspace')}
            data-testid="numpad-key-backspace"
          >
            <Delete className="w-7 h-7" />
          </button>
        </div>

        {/* Half button (pierna y pechuga only) */}
        {allowHalf && (
          <button
            onClick={() => handleKey('half')}
            className={`w-full h-14 rounded-2xl text-xl font-bold mb-3 transition-colors ${
              isHalf
                ? 'bg-amber-500 text-white'
                : 'bg-amber-100 text-amber-700 border-2 border-amber-300'
            }`}
            data-testid="numpad-key-half"
          >
            + ½ media pieza
          </button>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={onCancel}
            className="h-20 w-full bg-white text-zinc-900 border-2 border-zinc-300 rounded-2xl text-xl font-bold btn-press flex items-center justify-center gap-2"
            data-testid="numpad-btn-cancel"
          >
            <X className="w-6 h-6" />
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            className="h-20 w-full bg-blue-600 text-white rounded-2xl text-xl font-bold btn-press flex items-center justify-center gap-2"
            data-testid="numpad-btn-confirm"
          >
            <Check className="w-6 h-6" />
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}
